import { isDistributorAdmin } from '../utils/roles';
import React, { useEffect, useRef, useState } from 'react';
import { APP_VERSION } from '../appVersion';
import PageHeader from '../components/PageHeader';
import SettingsTabs from '../components/SettingsTabs';
import SizePreferences from '../components/resources/SizePreferences';
import { useAuth } from '../context/AuthContext';
import userService from '../services/userService';
import authService from '../services/authService';
import { useT } from '../i18n/LanguageContext';
import { accountId, captureSession, isSessionCurrent } from '../utils/authSession';
import { markSessionEnded } from '../utils/sessionEnded';
import '../styles/distributor-workspace.css';

/**
 * The server's rule for a new password, mirrored (auth/dto/change-password.dto.ts: MinLength(12)
 * plus one upper, one lower, one digit, one of @$!%*?&#). Checked here first so the person reads
 * one clear sentence instead of a class-validator dump. Server rejections use authored text;
 * raw password-response bodies are never rendered or logged.
 */
export const PASSWORD_RULE =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{12,}$/;
export const PASSWORD_RULE_TEXT =
  'Password must be at least 12 characters and include an uppercase letter, a lowercase letter, a number, and a special character (@$!%*?&#).';
export const PASSWORD_CHANGE_ERROR =
  'We could not change your password. Check your current password and the new password requirements, then try again.';
export const PASSWORD_CHANGE_RATE_LIMIT =
  'Too many password change attempts. Please wait a minute and try again.';

// The fallback is authored by this app. Response bodies may contain private diagnostics.
export function serverErrorText(_err, fallback) {
  return fallback;
}

export default function Settings() {
  const { user, updateUser, logout, sessionVersion } = useAuth();
  const masterDistributor = user?.role === 'master_distributor';
  const heading = useRef(null);
  useEffect(() => {
    if (masterDistributor) heading.current?.focus();
  }, [masterDistributor]);
  const readOnly = masterDistributor && Boolean(user?.distributorPreviewExpiresAt);
  const actorId = accountId(user);
  const viewKey = `${actorId}:${sessionVersion}:${user?.role}:${user?.distributor || ''}`;
  const currentView = useRef(viewKey);
  currentView.current = viewKey;
  const mounted = useRef(false);
  const profileFlight = useRef(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const t = useT();

  const [profile, setProfile] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
  });
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const passwordChangePending = useRef(null);
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' });
  const [passwordMsg, setPasswordMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    profileFlight.current = null;
    passwordChangePending.current = null;
    setChangingPassword(false);
    setSaving(false);
    setProfile({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
    });
    setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setProfileMsg({ type: '', text: '' });
    setPasswordMsg({ type: '', text: '' });
    // Profile updates within the same account must not reset an unsaved password draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewKey]);

  const initials =
    `${(profile.firstName || '').charAt(0)}${(profile.lastName || '').charAt(0)}`.toUpperCase();
  const displayName = `${profile.firstName} ${profile.lastName}`.trim();

  const handleProfileSave = async () => {
    if (readOnly || profileFlight.current) return;
    const session = captureSession();
    const initiatingView = viewKey;
    const reservation = {};
    const current = () =>
      mounted.current &&
      currentView.current === initiatingView &&
      profileFlight.current === reservation &&
      isSessionCurrent(session);
    const firstName = profile.firstName.trim();
    const lastName = profile.lastName.trim();
    // The server accepts an empty name; the sidebar would then fall back to the email and the
    // initials circle would be blank. Refuse it here.
    if (!firstName || !lastName) {
      setProfileMsg({ type: 'error', text: 'Enter both a first and a last name.' });
      return;
    }
    profileFlight.current = reservation;
    setSaving(true);
    setProfileMsg({ type: '', text: '' });
    try {
      const response = await userService.updateProfile({ firstName, lastName });
      if (!current()) return;
      const saved = response?.data?.data;
      if (
        !saved ||
        (actorId && saved._id !== actorId) ||
        saved.firstName !== firstName ||
        saved.lastName !== lastName
      )
        throw new Error('Profile update unconfirmed');
      // Sidebar name, initials and the avatar above all read the signed-in user: update it (and
      // localStorage) so the change shows without a sign-out.
      updateUser?.({ firstName, lastName });
      setProfile((p) => ({ ...p, firstName, lastName }));
      setProfileMsg({ type: 'success', text: 'Profile updated successfully!' });
    } catch (err) {
      if (current())
        setProfileMsg({ type: 'error', text: serverErrorText(err, 'Failed to update profile') });
    } finally {
      if (current()) setSaving(false);
      if (profileFlight.current === reservation) profileFlight.current = null;
    }
  };

  const handlePasswordChange = async () => {
    if (readOnly || passwordChangePending.current) return;
    if (!passwords.currentPassword || !passwords.newPassword) {
      setPasswordMsg({ type: 'error', text: 'Please fill in all password fields' });
      return;
    }
    if (passwords.newPassword !== passwords.confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    if (!PASSWORD_RULE.test(passwords.newPassword)) {
      setPasswordMsg({ type: 'error', text: PASSWORD_RULE_TEXT });
      return;
    }
    const session = captureSession();
    const reservation = {};
    passwordChangePending.current = reservation;
    setChangingPassword(true);
    setPasswordMsg({ type: '', text: '' });
    try {
      await authService.changePassword(passwords.currentPassword, passwords.newPassword, session);
      if (!isSessionCurrent(session)) return;
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      markSessionEnded('password_changed');
      // The backend revokes every session. Clear this session before authenticated UI can
      // continue, and invalidate refreshes that were already waiting to persist credentials.
      logout();
    } catch (err) {
      if (!isSessionCurrent(session)) return;
      const status = err?.response?.status;
      if ([400, 403, 409, 422, 429].includes(status)) {
        setPasswordMsg({
          type: 'error',
          text: status === 429 ? PASSWORD_CHANGE_RATE_LIMIT : PASSWORD_CHANGE_ERROR,
        });
      } else {
        // A lost response may follow a committed password change. Never replay the write or
        // keep using potentially revoked credentials. A newer login is unaffected.
        markSessionEnded(status === 401 ? 'expired' : 'password_change_uncertain');
        logout();
      }
    } finally {
      if (passwordChangePending.current === reservation) {
        passwordChangePending.current = null;
        if (isSessionCurrent(session)) setChangingPassword(false);
      }
    }
  };

  return (
    <div
      className={masterDistributor ? 'me-dw me-dw-account-settings mx-auto max-w-7xl' : undefined}
    >
      <PageHeader
        headingRef={masterDistributor ? heading : undefined}
        title={t('Settings')}
        subtitle={t('Manage your account settings and preferences.')}
      />
      <SettingsTabs />
      {readOnly && (
        <p className="mb-6 rounded-lg border border-[#d6dce8] bg-white p-4 text-sm" role="status">
          {t(
            'Browse this distributor workspace. Creating orders and changing accounts or settings are disabled.'
          )}
        </p>
      )}

      {/* Profile Section */}
      <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm p-6 mb-6">
        <p className="text-[12px] font-semibold text-[#64748b] uppercase tracking-wider mb-4">
          {t('Profile Information')}
        </p>

        {profileMsg.text && (
          <div
            className={`mb-4 p-3 rounded-lg text-sm ${
              profileMsg.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-600'
                : 'bg-red-50 border border-red-200 text-red-600'
            }`}
          >
            {t(profileMsg.text)}
          </div>
        )}

        <div className="flex items-center gap-4 mb-6">
          <div className="w-[60px] h-[60px] rounded-full bg-[#2c3850] flex items-center justify-center text-white text-xl font-semibold">
            {initials}
          </div>
          <div>
            <p className="text-[16px] font-semibold text-[#0f172a]">{displayName}</p>
            <p className="text-[14px] text-[#64748b]">{profile.email}</p>
          </div>
        </div>

        <div className="space-y-5">
          {masterDistributor && (
            <div>
              <label
                htmlFor="settings-username"
                className="block text-xs font-medium text-[#64748b] mb-2"
              >
                {t('Username')}
              </label>
              <input
                id="settings-username"
                value={user?.username || ''}
                disabled
                className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] bg-gray-50 cursor-not-allowed"
              />
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label
                htmlFor="settings-first-name"
                className="block text-xs font-medium text-[#64748b] mb-2"
              >
                {t('First Name')}
              </label>
              <input
                id="settings-first-name"
                type="text"
                disabled={saving || readOnly}
                value={profile.firstName}
                onChange={(e) => setProfile((p) => ({ ...p, firstName: e.target.value }))}
                className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
              />
            </div>
            <div>
              <label
                htmlFor="settings-last-name"
                className="block text-xs font-medium text-[#64748b] mb-2"
              >
                {t('Last Name')}
              </label>
              <input
                id="settings-last-name"
                type="text"
                disabled={saving || readOnly}
                value={profile.lastName}
                onChange={(e) => setProfile((p) => ({ ...p, lastName: e.target.value }))}
                className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="settings-email"
              className="block text-xs font-medium text-[#64748b] mb-2"
            >
              {t('Email')}
            </label>
            <input
              id="settings-email"
              type="email"
              value={profile.email}
              disabled
              className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] bg-gray-50 cursor-not-allowed"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 mt-6">
          <button
            onClick={handleProfileSave}
            disabled={saving || readOnly}
            className={
              masterDistributor
                ? 'me-dw-primary'
                : 'h-[42px] px-6 bg-[#0089ff] text-white text-sm font-semibold rounded-[10px] hover:bg-[#0077e6] transition-colors disabled:opacity-50'
            }
          >
            {saving ? t('Saving...') : t('Save Profile')}
          </button>
        </div>
      </div>

      {!readOnly && !isDistributorAdmin(user) && <SizePreferences key={viewKey} />}

      {/* Password Section */}
      <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm p-6">
        <p className="text-[12px] font-semibold text-[#64748b] uppercase tracking-wider mb-4">
          {t('Change Password')}
        </p>

        {passwordMsg.text && (
          <div
            className={`mb-4 p-3 rounded-lg text-sm ${
              passwordMsg.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-600'
                : 'bg-red-50 border border-red-200 text-red-600'
            }`}
          >
            {t(passwordMsg.text)}
          </div>
        )}

        <div className="space-y-5">
          <div>
            <label
              htmlFor="settings-current-password"
              className="block text-xs font-medium text-[#64748b] mb-2"
            >
              {t('Current Password')}
            </label>
            <input
              id="settings-current-password"
              disabled={changingPassword || readOnly}
              type="password"
              value={passwords.currentPassword}
              onChange={(e) => setPasswords((p) => ({ ...p, currentPassword: e.target.value }))}
              placeholder={t('Enter current password')}
              className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] placeholder:text-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label
                htmlFor="settings-new-password"
                className="block text-xs font-medium text-[#64748b] mb-2"
              >
                {t('New Password')}
              </label>
              <input
                id="settings-new-password"
                disabled={changingPassword || readOnly}
                type="password"
                value={passwords.newPassword}
                onChange={(e) => setPasswords((p) => ({ ...p, newPassword: e.target.value }))}
                placeholder={t('Enter new password')}
                className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] placeholder:text-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
              />
            </div>
            <div>
              <label
                htmlFor="settings-confirm-password"
                className="block text-xs font-medium text-[#64748b] mb-2"
              >
                {t('Confirm Password')}
              </label>
              <input
                id="settings-confirm-password"
                disabled={changingPassword || readOnly}
                type="password"
                value={passwords.confirmPassword}
                onChange={(e) => setPasswords((p) => ({ ...p, confirmPassword: e.target.value }))}
                placeholder={t('Confirm new password')}
                className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] placeholder:text-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 mt-6">
          <button
            onClick={handlePasswordChange}
            disabled={changingPassword || readOnly}
            className={
              masterDistributor
                ? 'me-dw-primary'
                : 'h-[42px] px-6 bg-[#0089ff] text-white text-sm font-semibold rounded-[10px] hover:bg-[#0077e6] transition-colors disabled:opacity-50'
            }
          >
            {changingPassword ? t('Changing...') : t('Change Password')}
          </button>
        </div>
      </div>

      {/* Which build is this? Nothing used to say, here or on mobile, so there
          was no way to tell whether a browser and a phone were running the same
          generation of the app. Pinned to the mobile APP_VERSION by
          appVersion.test.js. */}
      <p className="mt-8 text-center text-xs text-[#9299b4]">
        {t('MedEffects — Version {version}', { version: APP_VERSION })}
      </p>
    </div>
  );
}
